import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  getContact,
  listContactMessages,
  releaseContactHandoff,
  resetContact,
  updateContactContext,
  updateContactOnboarding,
} from '../../api/contacts';
import ContactContextSection from '../../components/ContactContextSection';
import ContactConversationPanel from '../../components/ContactConversationPanel';
import ContactOnboardingSection from '../../components/ContactOnboardingSection';
import SessionCard from '../../components/SessionCard';
import { getApiErrorMessage } from '../../utils/errors';
import {
  createContactSpaceLink,
  listContactIntegrations,
} from '../../api/contactSpace';

const STATUS_LABELS = {
  active: 'Ativo',
  blocked: 'Bloqueado',
  paused: 'Pausado',
};

const MESSAGES_PER_PAGE = 100;

function formatDateTime(iso) {
  if (!iso) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}

function StatusBadge({ status }) {
  const styles = {
    active: 'text-emerald-700 bg-emerald-50',
    blocked: 'text-red-700 bg-red-50',
    paused: 'text-amber-700 bg-amber-50',
  };

  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${
        styles[status] || 'text-content-subtle bg-slate-100'
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ContactDetail() {
  const { contactId } = useParams();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agentId') || '';

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [savingContext, setSavingContext] = useState(false);
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [generatingSpaceLink, setGeneratingSpaceLink] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState(() => new Set());
  const [sessionMessages, setSessionMessages] = useState({});

  const backPath = agentId ? `/contacts?agentId=${agentId}` : '/contacts';

  const loadContact = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }

    try {
      const data = await getContact(contactId);
      setContact(data);
      if (!silent) setError('');
    } catch (err) {
      if (!silent) {
        setError(getApiErrorMessage(err, 'Não foi possível carregar o contato.'));
        setContact(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    setExpandedSessions(new Set());
    setSessionMessages({});
  }, [contactId]);

  useEffect(() => {
    loadContact();
  }, [loadContact]);

  useEffect(() => {
    listContactIntegrations(contactId)
      .then((result) => setIntegrations(result.data || []))
      .catch(() => setIntegrations([]));
  }, [contactId]);

  const loadSessionMessages = useCallback(
    async (sessionId) => {
      setSessionMessages((prev) => ({
        ...prev,
        [sessionId]: { ...prev[sessionId], loading: true, error: null },
      }));

      try {
        const data = await listContactMessages(contactId, {
          page: 1,
          perPage: MESSAGES_PER_PAGE,
          sessionId,
        });
        setSessionMessages((prev) => ({
          ...prev,
          [sessionId]: { data: data.data, meta: data.meta, loading: false, error: null },
        }));
      } catch (err) {
        setSessionMessages((prev) => ({
          ...prev,
          [sessionId]: {
            ...prev[sessionId],
            loading: false,
            error: getApiErrorMessage(err, 'Não foi possível carregar as mensagens.'),
          },
        }));
      }
    },
    [contactId]
  );

  const handleToggleSession = useCallback((sessionId) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  }, []);

  const handleReleaseHandoff = async () => {
    const confirmed = window.confirm(
      `Liberar o contato ${contact.external_id}? O agente voltará a responder automaticamente.`
    );
    if (!confirmed) return;

    setReleasing(true);
    setError('');
    setSuccess('');

    try {
      const response = await releaseContactHandoff(contactId);
      if (response.success && response.contact) {
        setContact(response.contact);
        setSuccess('Contato liberado com sucesso.');
      } else {
        setError('Não foi possível liberar o contato.');
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível liberar o contato.'));
    } finally {
      setReleasing(false);
    }
  };

  const handleResetContact = async () => {
    const confirmed = window.confirm(
      `Resetar o contato ${contact.external_id}? Isso apagará onboarding, mensagens, sessões, contexto acumulado e handoff. O número continuará vinculado ao agente.`
    );
    if (!confirmed) return;

    setResetting(true);
    setError('');
    setSuccess('');

    try {
      const data = await resetContact(contactId);
      setContact(data.contact);
      setExpandedSessions(new Set());
      setSessionMessages({});
      const deleted = data.deleted;
      setSuccess(
        `Contato resetado. ${deleted.messages} mensagem(ns), ${deleted.sessions} sessão(ões) removidas.`
      );
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível resetar o contato.'));
    } finally {
      setResetting(false);
    }
  };

  const handleSaveContext = async (progressiveSummary) => {
    setSavingContext(true);
    setError('');
    setSuccess('');

    try {
      const updated = await updateContactContext(contactId, progressiveSummary);
      setContact(updated);
      setSuccess('Contexto atualizado com sucesso.');
      return true;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível atualizar o contexto.'));
      return false;
    } finally {
      setSavingContext(false);
    }
  };

  const handleSaveOnboarding = async ({ onboardingData, onboardingCompleted }) => {
    setSavingOnboarding(true);
    setError('');
    setSuccess('');

    try {
      const updated = await updateContactOnboarding(contactId, {
        onboardingData,
        onboardingCompleted,
      });
      setContact(updated);
      setSuccess('Onboarding atualizado com sucesso.');
      return true;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível atualizar o onboarding.'));
      return false;
    } finally {
      setSavingOnboarding(false);
    }
  };

  const handleCopySpaceLink = async () => {
    setGeneratingSpaceLink(true);
    setError('');
    setSuccess('');
    try {
      const result = await createContactSpaceLink(contactId);
      await navigator.clipboard.writeText(result.url);
      setSuccess('Link privado do espaço copiado. Ele expira automaticamente.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível gerar o link do espaço.'));
    } finally {
      setGeneratingSpaceLink(false);
    }
  };

  const handleMetaMessageSent = async (response) => {
    setSuccess(
      response?.dispatch?.method === 'template'
        ? 'Template Meta enviado. A IA está pausada neste contato.'
        : 'Mensagem enviada. A IA está pausada neste contato.'
    );
    if (response?.contact) {
      setContact(response.contact);
      return;
    }
    await loadContact({ silent: true });
  };

  if (loading) {
    return (
      <div className="surface-card px-4 py-12 text-center">
        <p className="text-content-muted text-sm">Carregando contato...</p>
      </div>
    );
  }

  if (error && !contact) {
    return (
      <div className="space-y-4">
        <Link to={backPath} className="text-sm text-forge-600 hover:text-forge-800">
          ← Voltar para contatos
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="space-y-4">
        <Link to={backPath} className="text-sm text-forge-600 hover:text-forge-800">
          ← Voltar para contatos
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">Contato não encontrado.</p>
        </div>
      </div>
    );
  }

  const pastSessions = (contact.sessions || []).filter(
    (session) => session.status !== 'active'
  );

  const metaParts = [
    contact.onboarding_completed ? 'Onboarding ok' : 'Onboarding pendente',
    contact.created_at && `Criado em ${formatDateTime(contact.created_at)}`,
    contact.updated_at && `Atualizado em ${formatDateTime(contact.updated_at)}`,
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Link to={backPath} className="text-sm text-forge-600 hover:text-forge-800">
          ← Voltar para contatos
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-2xl font-bold text-content-primary">{contact.external_id}</h1>
              <StatusBadge status={contact.status} />
            </div>
            {metaParts.length > 0 && (
              <p className="text-sm text-content-muted mt-1">{metaParts.join(' · ')}</p>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleResetContact}
              disabled={resetting || releasing}
              className="text-sm text-red-600 hover:text-red-700 disabled:opacity-40 shrink-0"
            >
              {resetting ? 'Resetando...' : 'Resetar contato'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-sm text-emerald-700">{success}</p>
        </div>
      )}

      <ContactConversationPanel
        contactId={contactId}
        contact={contact}
        onReleaseHandoff={handleReleaseHandoff}
        releasing={releasing}
        onSent={handleMetaMessageSent}
      />

      <ContactContextSection
        context={contact.context}
        onSave={handleSaveContext}
        saving={savingContext}
      />

      <ContactOnboardingSection
        onboardingData={contact.onboarding_data}
        onboardingCompleted={contact.onboarding_completed}
        onSave={handleSaveOnboarding}
        saving={savingOnboarding}
      />

      <section className="surface-card p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-content-primary">
              Espaço do contato
            </h2>
            <p className="mt-1 text-xs text-content-muted">
              Aplicativos autorizados pela própria pessoa.
            </p>
          </div>
          <button
            type="button"
            onClick={handleCopySpaceLink}
            disabled={generatingSpaceLink}
            className="btn-secondary px-4 py-2 text-sm"
          >
            {generatingSpaceLink ? 'Gerando…' : 'Copiar link privado'}
          </button>
        </div>
        {integrations.length > 0 ? (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {integrations.map((integration) => (
              <li
                key={integration.app_id}
                className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <span className="font-medium text-slate-800">{integration.name}</span>
                <span
                  className={
                    integration.status === 'connected'
                      ? 'text-emerald-700'
                      : 'text-content-muted'
                  }
                >
                  {integration.status === 'connected' ? 'Conectado' : 'Desconectado'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-xl border border-dashed border-slate-300 px-4 py-5 text-center text-sm text-content-muted">
            Nenhum aplicativo conectado.
          </p>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-content-primary">
              Conversas anteriores
            </h2>
            <p className="text-xs text-content-muted mt-1">
              Sessões encerradas — clique para ver resumo e mensagens
            </p>
          </div>
          <p className="text-sm text-content-subtle">
            {pastSessions.length} sessão
            {pastSessions.length !== 1 ? 'ões' : ''}
          </p>
        </div>

        {pastSessions.length > 0 ? (
          <ul className="space-y-2">
            {pastSessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                expanded={expandedSessions.has(session.id)}
                messagesState={sessionMessages[session.id]}
                onToggle={handleToggleSession}
                onLoadMessages={loadSessionMessages}
              />
            ))}
          </ul>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center">
            <p className="text-content-muted text-sm">
              Nenhuma conversa encerrada até agora.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

export default ContactDetail;
